import * as tslib_1 from "tslib";
import { OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { map, take, takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { addSeconds, differenceInSeconds, format, isValid } from 'date-fns';
import { SessionStorage, SessionStorageService } from 'ngx-webstorage';
import { ConfigService } from '../config/config.service';
import { DialogService, ConfirmationDialogOptions } from '../dialogs/dialogs.service';
import { SplashService } from '../splash/splash.service';
import { TimerService } from 'services/timer/timer.service';
import { UserService } from '../user/user.service';
import { VERSION_NUMBER, BUILD_TIME } from '../version/version.service';
import { UnsavedChangesService } from 'routes/routes.guard';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../config/config.service";
import * as i3 from "../user/user.service";
import * as i4 from "@angular/router";
import * as i5 from "ngx-webstorage";
import * as i6 from "../dialogs/dialogs.service";
import * as i7 from "../splash/splash.service";
import * as i8 from "../timer/timer.service";
import * as i9 from "../../routes/routes.guard";
var AuthService = /** @class */ (function () {
    function AuthService(http, configService, userService, router, sessionStorage, dialogService, splashService, timerService, unsavedChangesService) {
        var _this = this;
        this.http = http;
        this.configService = configService;
        this.userService = userService;
        this.router = router;
        this.sessionStorage = sessionStorage;
        this.dialogService = dialogService;
        this.splashService = splashService;
        this.timerService = timerService;
        this.unsavedChangesService = unsavedChangesService;
        this._subRelease = new Subject();
        this._authtimer = null;
        this.systemOnline = false;
        this.HasVersionUpdate = new Subject();
        this.SystemOnlineSubject = new Subject();
        this.BuildTime = new Subject();
        this.VersionNumber = new Subject();
        this.AuthRefreshDeadline = this.nextAuthRefresh ? new Date(this.nextAuthRefresh) : null;
        this.IsRefreshing = false;
        var btime = new Date(BUILD_TIME);
        if (isValid(btime)) {
            this.BuildTime.next(format(btime, 'MMM. DD, YYYY h:mm t'));
        }
        this.VersionNumber.next(VERSION_NUMBER);
        this.SystemOnlineSubject.next({
            SystemMessage: '',
            Status: this.systemOnline,
            IRISVersion: VERSION_NUMBER,
        });
        // Set up a restartable timer for use in authtoken refreshing
        this._authtimer = this.timerService.CreateTimer('authtoken', 15 * 1000);
        if (this._authtimer) {
            this._authtimer.Timer.subscribe(function (tick) {
                // Refresh the authtoken.
                _this.AuthTokenRefresh();
            });
        }
        // Set up the subscription to watch for the user being updated, so we can tell if we need to boot up auth token refreshing.
        this.userService.UserSubject.pipe(takeUntil(this._subRelease)).subscribe(function (user) {
            // See if we have a User object already known to us. If so, this was a refresh of the page most likely,
            // and thus we need to kick start authtoken refreshing again.
            if (user && !_this.IsRefreshing && user.AuthToken) {
                _this.StartAuthTokenRefreshing();
            }
        });
    }
    AuthService.prototype.ngOnDestroy = function () {
        if (this._authtimer) {
            this._authtimer.StopTimer();
            this._authtimer = null;
        }
        this._subRelease.next();
        this._subRelease.complete();
    };
    // #region Auth Token Refresh Method
    AuthService.prototype.AuthTokenRefresh = function (checkRecent) {
        var self = this;
        // Now retrieve the auth token for the currently logged in user, stored in the userService
        var auth = self.userService.User.AuthToken;
        // Send a request to the system to check to see if the user's "Recent Use" flag is set. If it is,
        // we will refresh their auth token and reset all countdown timers.
        self.CheckRecentUse(auth).subscribe(function (recentUse) {
            if (recentUse === true) {
                // Call the service which will refresh the auth token.
                self.RefreshAuthToken(auth).subscribe(function (newAuthPacket) {
                    var newAuth = newAuthPacket.AuthToken;
                    if (newAuthPacket && newAuthPacket.IRISVersion) {
                        // We have version information. Does it match the version known here in the TypeScript?
                        // If not, we set the update flag.
                        if (VERSION_NUMBER !== newAuthPacket.IRISVersion) {
                            // Mismatch. Set the flag.
                            self.HasVersionUpdate.next(true);
                        }
                    }
                    // We have passed the check to stay using the system. Update the user variable in the
                    // userService to reflect this.
                    self.userService.UpdateAuthToken(newAuth);
                    // If there is no flag signal set preventing this loop from continuing, then we can set up
                    // the next iteration with a timeout now.
                    if (!self.AuthTokenCancelFlag) {
                        if (self._authtimer) {
                            self._authtimer.StartTimer(AuthService.AUTHCHECKTIME * 1000);
                        }
                        // Set the refresh deadline app value
                        var Now = new Date();
                        self.AuthRefreshDeadline = addSeconds(Now, AuthService.AUTHCHECKTIME);
                        self.nextAuthRefresh = self.AuthRefreshDeadline.toISOString();
                    }
                }, function (error) {
                    // An error occurred. Attempt to retrieve it.
                    self.HandleServiceError(error, self.userService.User);
                });
            }
            else {
                // We either could not retrieve the "Recent Use" flag, or we DID and it was false. In either case,
                // we must begin a countdown process to log out the user from the system.
                // First, disable the current auth token refresh cycling. We won't restart that until we get
                // feedback from the user.
                self.CancelAuthTokenRefreshing();
                self.dialogService
                    .OpenCountdownDialog({
                    TitleText: 'Session Inactivity Logout',
                    MessageText: "You are about to be automatically logged out of the system due to inactivity. " +
                        "If you wish to stay logged in to the site, please click the \"Stay Logged In\" button below.",
                    OKText: 'Stay Logged In',
                    CancelText: 'Log Out',
                    CountdownTime: AuthService.LOGOUTWARNINGTIME,
                })
                    .subscribe(function (res) {
                    if (res === true) {
                        // User wants to stay logged in - Restart the auth token refresh cycling.
                        self.RefreshAuthToken(auth).subscribe(function (newAuthPacket) {
                            var newAuth = newAuthPacket.AuthToken;
                            if (newAuthPacket && newAuthPacket.IRISVersion) {
                                // We have version information. Does it match the version known here in the TypeScript?
                                // If not, set the update flag.
                                if (VERSION_NUMBER !== newAuthPacket.IRISVersion) {
                                    // Mismatch. Set the flag.
                                    self.HasVersionUpdate.next(true);
                                }
                            }
                            // We have a new valid auth token to use. Update the user object accordingly with the new token.
                            self.userService.UpdateAuthToken(newAuth);
                        }, function (error) {
                            var body = error.json();
                            body =
                                (body && body.Message) ||
                                    body ||
                                    "An error occurred while trying to validate your active connection to the system. " +
                                        "You have been logged out.";
                            self.splashService.SnackbarError(body, 5000);
                        });
                    }
                    else {
                        // User wishes to logout, or the timer expired. Either way, log them out.
                        // Disable change tracking - sorry, but you've been timed out.
                        self.unsavedChangesService.ClearTracking();
                        // Now log them out.
                        self.Logout(false, 1);
                    }
                });
            }
        }, function (error) {
            self.CancelAuthTokenRefreshing();
            self.Logout(false);
        });
    };
    // #endregion
    // #region Cancel Auth Token Refreshing Method
    AuthService.prototype.CancelAuthTokenRefreshing = function () {
        // If possible, use the handle to cancel the timeout from happening
        if (this._authtimer) {
            this._authtimer.PauseTimer();
        }
        // Cancel the flag, handle, and deadline to refresh
        this.AuthTokenRefreshHandle = null;
        this.AuthTokenCancelFlag = null;
        this.AuthRefreshDeadline = null;
        this.IsRefreshing = false;
        this.nextAuthRefresh = null;
    };
    // #endregion
    // #region Change Impersonation Target Service Call
    AuthService.prototype.ChangeImpersonationTarget = function (AuthToken, SAPID) {
        return this.http
            .get("" + this.configService.hostAddress +
            ("Api/Services/Authentication/ChangeImpersonationTarget?AuthToken=" + (AuthToken || '') + "&SAPID=" + (SAPID || '')))
            .pipe(map(function (userinfo) {
            // If we have roles, before we return we should number them so we can recover in case of page refreshing.
            if (userinfo.Roles && !!userinfo.Roles.length) {
                userinfo.Roles.forEach(function (role, idx) {
                    role.RoleNumber = idx;
                });
            }
            return userinfo;
        }));
    };
    // #endregion
    // #region Check Recent Use Service Method - This method simply calls the database to see if the user has done anything
    AuthService.prototype.CheckRecentUse = function (AuthToken) {
        return this.http.get(this.configService.hostAddress + ("Api/Services/Authentication/GetRecentUse?AuthToken=" + (AuthToken || '')));
    };
    // #endregion
    // #region Get IRIS Version Number Service Call
    AuthService.prototype.GetIRISVersionNumber = function () {
        return this.http.get(this.configService.hostAddress + "Api/Services/Authentication/GetIRISVersionNumber");
    };
    // #endregion
    // #region Handle Service Error Service Call
    AuthService.prototype.HandleServiceError = function (error, user) {
        var _this = this;
        var self = this;
        var status = null;
        var msg = null;
        if (error instanceof HttpErrorResponse) {
            status = error.status;
            var body = error && error.error;
            msg =
                body.ExceptionMessage || body.Message || body || (typeof body === 'string' ? body : 'An unknown error has occurred.');
        }
        else {
            var errorPacket = error.message || error;
            if (errorPacket != null && errorPacket !== '') {
                errorPacket = JSON.parse(errorPacket);
            }
            msg =
                errorPacket != null && errorPacket instanceof Object
                    ? errorPacket.ExceptionMessage || errorPacket.Message || error
                    : errorPacket || 'An unknown error has occurred.';
        }
        this.splashService.CloseSplash();
        var handled = false;
        if (status === 401) {
            // This is an unauthorized response. Check the msg field. If it matches the string 'AuthToken Expired',
            // the user's authtoken has expired and the user needs to log back in.
            if (msg === 'AuthToken Expired') {
                // It's an expired authtoken. Set the flag that we've handled this error, and we'll do a relogin dialog.
                handled = true;
                // Cancel authtoken refreshing - pointless now anyway
                self.CancelAuthTokenRefreshing();
                // Pop up a dialog box to let the user log in again and stay on this screen.
                self.dialogService
                    .OpenLoginBoxDialog({
                    TitleText: 'Session Timed Out - Please Re-Login',
                    MessageText: "Your current session to the IRIS System appears to have timed out due to inactivity or " +
                        "network disconnection. Before you can continue, please login again using the login form " +
                        "provided below.<br /><br /><strong><span style=\"color:red\">Note: </span></strong>The operation you were " +
                        "just attempting did not succeed, but your data on this page (if any) has not been lost. After you log " +
                        "back in to the IRIS System, you may attempt the operation once again.",
                    OKText: 'Login',
                    CancelText: 'Cancel',
                    Username: user && user.AccountName,
                })
                    .subscribe(function (credentials) {
                    if (credentials != null) {
                        // Attempt to log back in using the provided credentials
                        self.RevalidateSession(credentials.Username, credentials.Password, user && user.AuthToken, user && user.SAPID, user != null ? user.IsImpersonation : false).subscribe(function (userinfo) {
                            if (userinfo) {
                                // We can now update the authtoken we just received for this user.
                                self.userService.UpdateAuthToken(userinfo.AuthToken);
                                // Toast successful revalidation
                                self.splashService.SnackbarSuccess('Session revalidated successfully. Please retry the last request.', 6000);
                            }
                        }, function (rerror) {
                            self.HandleServiceError(rerror);
                        });
                    }
                    else {
                        // User cancelled. First, disable any pending unsaved changes
                        self.unsavedChangesService.ClearTracking();
                        // Now logout.
                        self.Logout(false);
                    }
                });
            }
        }
        if (!handled && status === 408) {
            // This is a Request Timeout response, indicating the user's session has expired.
            handled = true;
            this.dialogService
                .OpenMessageBoxDialog({
                TitleText: 'Session Timed Out',
                MessageText: "Your current session to the IRIS System has timed out and your login access has been closed. " +
                    "In order to continue using the IRIS System you will need to login again to the system.",
            })
                .subscribe(function (res) {
                // Force an unconfirmed logout to clear user stored values and return to the login screen.
                _this.Logout(false, 2);
            });
        }
        if (!handled) {
            // Something else, which should be snackbar displayed.
            this.splashService.SnackbarError(msg, 8000);
        }
    };
    // #endregion
    // #region Impersonate User Service Method
    AuthService.prototype.ImpersonateUser = function (AuthToken, CurrentRole, Username, ImpersonatorUserID, ImpersonatorSAPID, ImpersonatorPositionNo) {
        return this.http
            .post(this.configService.hostAddress + "Api/Services/Authentication/ImpersonateUser", {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            Username: Username || '',
            ImpersonatorUserID: ImpersonatorUserID || '',
            ImpersonatorSAPID: ImpersonatorSAPID || '',
            ImpersonatorPositionNo: ImpersonatorPositionNo || '',
        })
            .pipe(map(function (userinfo) {
            // If we have roles, before we return we should number them so we can recover in case of page refreshing.
            if (userinfo.Roles && !!userinfo.Roles.length) {
                userinfo.Roles.forEach(function (role, idx) {
                    role.RoleNumber = idx;
                });
            }
            return userinfo;
        }));
    };
    // #endregion
    // #region Logout From System Service Call
    AuthService.prototype.LogoutFromSystem = function (AuthToken, Reason) {
        return this.http.get(this.configService.hostAddress + ("Api/Services/Authentication/Logout?AuthToken=" + (AuthToken || '') + "&Reason=" + (Reason || '')));
    };
    // #endregion
    // #region Logout Service Call
    AuthService.prototype.Logout = function (confirmation, reason) {
        var self = this;
        var confObs = null;
        if (confirmation === true) {
            // Pop up a confirmation box to confirm desire to logout.
            var opts = {
                TitleText: 'Confirmation Requested',
                MessageText: 'You are about to log out of the site. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            };
            confObs = self.dialogService.OpenConfirmationDialog(opts);
        }
        else {
            // No dialog needed.
            confObs = of(true).pipe(take(1));
        }
        confObs.pipe(takeUntil(self._subRelease)).subscribe(function (result) {
            if (result === true) {
                // User is opting to log out. Proceed.
                // Kill off any tracking in place.
                self.unsavedChangesService.ClearTracking();
                self.LogoutFromSystem(self.userService.User.AuthToken, reason).subscribe();
                // Stop authtoken refreshing
                self.CancelAuthTokenRefreshing();
                // Null out the current user.
                self.userService.ClearUser();
                // Snackbar the logout if this was user initiated (reason null or 0)
                if (reason == null || reason === 0) {
                    self.splashService.SnackbarSuccess('Successfully logged out.', 5000);
                }
                // Close out all remaining open dialogs
                self.dialogService.CloseAllDialogs();
                // Navigate to the login state.
                self.router.navigate(["/login"]);
            }
        });
    };
    // #endregion
    // #region Process Login Service Method
    AuthService.prototype.ProcessLogin = function (user) {
        var self = this;
        // Set the currently logged in user information.
        self.userService.SetUser(user);
        if (user && user.Roles && !!user.Roles.length) {
            var topRole = user.Roles[0];
            self.userService.SetUserCurrentRole(topRole.RoleName);
            // if (topRole.RoleName === 'Developer') {
            //     // Is a developer, send to the developer "home" page
            //     self.userService.SetUserCurrentRole('Developer');
            //     return;
            // } else if (topRole.IsSystemAdmin) {
            //     self.userService.SetUserCurrentRole('SystemAdmin');
            //     return;
            // } else if (topRole.IsPrincipal) {
            //     self.userService.SetUserCurrentRole('Principal');
            //     return;
            // } else if (topRole.IsAssistantPrincipal) {
            //     self.userService.SetUserCurrentRole('AssistantPrincipal');
            //     return;
            // } else if (topRole.IsJudge) {
            //     self.userService.SetUserCurrentRole('Judge');
            //     return;
            // } else if (topRole.IsDesignee) {
            //     self.userService.SetUserCurrentRole('Designee');
            //     return;
            // } else if (topRole.IsTeacherNominee) {
            //     self.userService.SetUserCurrentRole('TeacherNominee');
            //     return;
            // } else if (topRole.IsSchoolNominee) {
            //     self.userService.SetUserCurrentRole('SchoolNominee');
            //     return;
            // } else {
            //     // No role match found. Reject politely.
            //     self.dialogService.OpenMessageBoxDialog({
            //         TitleText: 'Unable to login',
            //         MessageText: `We're sorry, the IRIS System is currently only available to school administrators, ` +
            //         `select designees, district administrators, awards nominees, and awards judges who are active in the ` +
            //         `IRIS System. If this is an error, please contact the Help Desk for more assistance.`
            //     }).subscribe(() => {
            //         self.Logout(null, null);
            //     });
            // }
        }
        else {
            // No role found. Reject politely.
            self.dialogService
                .OpenMessageBoxDialog({
                TitleText: 'Unable to login',
                MessageText: "We're sorry, the IRIS System is currently only available to school board members, district staff, " +
                    "and select supervisors who are active in the " +
                    "IRIS System. If this is an error, please contact the Help Desk for more assistance.",
            })
                .subscribe(function () {
                self.Logout(null, null);
            });
        }
    };
    // #endregion
    // #region Refresh Authentication Token Service Method
    AuthService.prototype.RefreshAuthToken = function (AuthToken) {
        return this.http.get(this.configService.hostAddress + ("Api/Services/Authentication/RefreshAuthToken?AuthToken=" + (AuthToken || '')));
    };
    // #endregion
    // #region Revalidate Session Service Call
    AuthService.prototype.RevalidateSession = function (Username, Password, AuthToken, SAPID, IsImpersonation) {
        return this.http
            .post(this.configService.hostAddress + "Api/Services/Authentication/RevalidateSession", {
            Username: Username || '',
            Password: Password || '',
            AuthToken: AuthToken || '',
            SAPID: SAPID || '',
            IsImpersonation: IsImpersonation || '',
        })
            .pipe(map(function (userinfo) {
            // If we have roles, before we return we should number them so we can recover in case of page refreshing.
            if (userinfo.Roles && !!userinfo.Roles.length) {
                userinfo.Roles.forEach(function (role, idx) {
                    role.RoleNumber = idx;
                });
            }
            return userinfo;
        }));
    };
    // #endregion
    // #region Set System Online Status
    AuthService.prototype.SetSystemOnlineStatus = function (status) {
        this.systemOnline = (status && status.Status) || false;
        this.SystemOnlineSubject.next(status);
    };
    // #endregion
    // #region Start AuthToken Refreshing Service Call
    AuthService.prototype.StartAuthTokenRefreshing = function () {
        // Initiate the auth token refresh cycling by calling the refresh method the first time
        this.AuthTokenCancelFlag = false;
        // Check real quick for a present value for AuthRefreshDeadline. If that exists, then it indicates the time when we absolutely
        // need to refresh the auth token, so we should set the refresh handle time to just before then. Otherwise, this is probably a
        // first login so use the default time.
        var timeToRefresh = AuthService.AUTHCHECKTIME;
        if (this.nextAuthRefresh) {
            // Read the deadline time to refresh from the session storage, convert into a Date object.
            this.AuthRefreshDeadline = new Date(this.nextAuthRefresh);
        }
        else {
            // Nothing available from session storage, so queue up a fresh refresh clock.
            this.AuthRefreshDeadline = addSeconds(new Date(), AuthService.AUTHCHECKTIME);
        }
        if (this.AuthRefreshDeadline && isValid(this.AuthRefreshDeadline)) {
            // Find the time difference in seconds between now and when we are due to refresh.
            var timediff = differenceInSeconds(this.AuthRefreshDeadline, new Date());
            // We will always use the lesser of the next known time to refresh and the usual delay time. This is in case we are refreshing
            // the browser and need to restart refresh cycle, as opposed to fresh starting cycles.
            if (timediff < timeToRefresh) {
                timeToRefresh = timediff;
            }
            if (timeToRefresh <= 0) {
                timeToRefresh = 1;
            } // Ensure at least a 1 second wait.
        }
        // Begin the timer, if one isn't already set
        if (this._authtimer) {
            this._authtimer.StartTimer(timeToRefresh * 1000);
        }
        // Set the refresh deadline app value
        var Now = new Date();
        this.AuthRefreshDeadline = addSeconds(Now, timeToRefresh);
        this.IsRefreshing = true;
        this.nextAuthRefresh = this.AuthRefreshDeadline.toISOString();
    };
    // #endregion
    // #region Test Connection Service Call
    AuthService.prototype.TestConnection = function () {
        // Call the WebApi to see if anyone is home.
        return this.http.get(this.configService.hostAddress + 'Api/Services/Authentication/TestConnection');
    };
    // #endregion
    // #region Validate User Service Call
    AuthService.prototype.ValidateUser = function (username, password, impersonatedUsername, trainingMode, screen, winres) {
        return this.http
            .post(this.configService.hostAddress + 'Api/Services/Authentication/ValidateUser', {
            Username: username || '',
            Password: password || '',
            ImpersonatedUsername: impersonatedUsername || '',
            TrainingMode: trainingMode || '',
            Screen: screen,
            Window: winres || '',
        })
            .pipe(map(function (userinfo) {
            // If we have roles, before we return we should number them so we can recover in case of page refreshing.
            if (userinfo.Roles && !!userinfo.Roles.length) {
                userinfo.Roles.forEach(function (role, idx) {
                    role.RoleNumber = idx;
                });
            }
            return userinfo;
        }));
    };
    // 17 minutes (time period to wait between successive checks of whether the auth token is recently used)
    AuthService.AUTHCHECKTIME = 17 * 60;
    // 2 minutes, 50 seconds (time period to wait before logging the user out automatically). Roughly 20 minutes total between check
    // and autologout
    AuthService.LOGOUTWARNINGTIME = 170;
    AuthService.ngInjectableDef = i0.defineInjectable({ factory: function AuthService_Factory() { return new AuthService(i0.inject(i1.HttpClient), i0.inject(i2.ConfigService), i0.inject(i3.UserService), i0.inject(i4.Router), i0.inject(i5.SessionStorageService), i0.inject(i6.DialogService), i0.inject(i7.SplashService), i0.inject(i8.TimerService), i0.inject(i9.UnsavedChangesService)); }, token: AuthService, providedIn: "root" });
    tslib_1.__decorate([
        SessionStorage(),
        tslib_1.__metadata("design:type", String)
    ], AuthService.prototype, "nextAuthRefresh", void 0);
    tslib_1.__decorate([
        SessionStorage(),
        tslib_1.__metadata("design:type", Object)
    ], AuthService.prototype, "systemOnline", void 0);
    return AuthService;
}());
export { AuthService };
// #region Custom Classes
// #region Auth Packet Class
var AuthPacket = /** @class */ (function () {
    function AuthPacket() {
    }
    return AuthPacket;
}());
export { AuthPacket };
// #endregion
// #region Shutdown Status Info Class
var ShutdownStatusInfo = /** @class */ (function () {
    function ShutdownStatusInfo() {
    }
    return ShutdownStatusInfo;
}());
export { ShutdownStatusInfo };
// #endregion
// #endregion
var TestConnectionPacket = /** @class */ (function () {
    function TestConnectionPacket() {
    }
    return TestConnectionPacket;
}());
export { TestConnectionPacket };
